import { Field, Form, Formik } from 'formik';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import * as Yup from 'yup';
import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { DropdownComponent } from '../../../components/prime-formik';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { growlErrors } from '../../../utils/NotificationUtil';
import { useMutation } from '@apollo/react-hooks';
import { BUY_CREDITS_MUTATION } from '../services/buy-credits.service';

const i18nMessages = defineMessages({
  buyCredits: {
    id: 'menu.creditsManagement.buy-credits',
    defaultMessage: 'Buy Credits',
  },
  sendPaymentLink: {
    id: 'buy-credits.send-payment-link',
    defaultMessage: 'Send payment link',
  },
  purchasingFor: {
    id: 'buy-credits.purchasing-for',
    defaultMessage: 'Purchasing for: ',
  },
  purchaser: {
    id: 'buy-credits.purchaser',
    defaultMessage: 'Purchaser: ',
  },
  price: {
    id: 'buy-credits.price',
    defaultMessage: 'Price: ',
  },
  selectQuantity: {
    id: 'buy-credits.select-quantity',
    defaultMessage: 'Select quantity',
  },
  payNow: {
    id: 'account.generic.pay-now',
    defaultMessage: 'Pay now',
  },
  successfulPayment: {
    id: 'buy-credits.successful-payment',
    defaultMessage: '{amountOfCredits} Credits have been delivered to {accountName}',
  },
  cancelledPayment: {
    id: 'buy-credits.cancelled-payment',
    defaultMessage: 'The payment was cancelled and no Credits have been transferred to your account',
  },
  paymentGenericError: {
    id: 'buy-credits.payment-generic-error',
    defaultMessage: 'There was an error trying to process your payment',
  },
  thankYou: {
    id: 'buy-credits.thank-you',
    defaultMessage: 'Thank you!',
  },
  sorry: {
    id: 'buy-credits.sorry',
    defaultMessage: "We're sorry",
  },
  errorHeader: {
    id: 'buy-credits.error',
    defaultMessage: 'Error!',
  },
});

const BuyCreditsForm = ({
  viewer,
  accountDisplayName,
  computedCostInUsd,
  selectedCreditOption,
  creditValues,
  setSelectedCreditOption,
  showPaymentDialog,
}) => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const initialValues = {
    amountOfCredits: selectedCreditOption,
  };
  const validationSchema = Yup.object().shape({
    amountOfCredits: Yup.number().required(),
  });

  const creditOptions = creditValues.map((creditValue) => ({
    value: creditValue.credit,
    label: creditValue.credit.toLocaleString(),
  }));

  const [buyCreditsMutation] = useMutation(BUY_CREDITS_MUTATION);

  const buyCredits = async (values, resetForm) => {
    try {
      const {
        data: {
          buyCredits: { url },
        },
      } = await buyCreditsMutation({
        variables: {
          input: {
            accountId: currentAccount,
            amountOfCredits: values.amountOfCredits,
          },
        },
      });

      if (url) {
        window.open(url, '_blank', 'noreferrer');
      }
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    } finally {
      resetForm();
    }
  };

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-sm-12 p-md-6">
            <Panel header={formatMessage(i18nMessages.buyCredits)}>
              <div className="padding-bottom">
                <strong>{formatMessage(i18nMessages.purchaser)}</strong>
                <span>
                  {viewer.firstName} {viewer.lastName}
                </span>
              </div>
              <div className="padding-bottom">
                <strong>{formatMessage(i18nMessages.purchasingFor)}</strong>
                <span>{accountDisplayName}</span>
              </div>
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, { resetForm }) => {
                  buyCredits(values, resetForm);
                }}
              >
                {({ isSubmitting, handleSubmit, setFieldValue }) => (
                  <Form onSubmit={handleSubmit}>
                    <div className="padding-bottom">
                      <div className="input-label">{formatMessage(i18nMessages.selectQuantity)}</div>
                      <div className="padding-bottom">
                        <Field
                          id="amountOfCredits"
                          name="amountOfCredits"
                          component={DropdownComponent}
                          options={creditOptions}
                          onChange={({ value }) => {
                            setFieldValue('amountOfCredits', value);
                            setSelectedCreditOption(value);
                          }}
                        />
                      </div>
                      <div className="padding-bottom">
                        <strong>{formatMessage(i18nMessages.price)}</strong>
                        <span>{`$${computedCostInUsd}`}</span>
                      </div>
                    </div>
                    <div>
                      <Button type="submit" disabled={isSubmitting} label={formatMessage(i18nMessages.payNow)} />
                      <Button
                        type="button"
                        label={formatMessage(i18nMessages.sendPaymentLink)}
                        onClick={showPaymentDialog}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyCreditsForm;
